import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import { Boxes, Loader2, PackageMinus, SkipForward, AlertTriangle } from "lucide-react";

/**
 * Auto-stock deduction confirmation modal.
 *
 * Flow:
 *   1. Fetch /api/shipments/:id/inventory-suggestions on open
 *   2. If empty → resolve immediately ("nothing to deduct") so caller can proceed
 *   3. Else → render large checklist with editable qty + "Deduct & Save" / "Skip Deduction"
 *
 * Props:
 *   shipmentId: string (required)
 *   open: boolean
 *   onResolved: (result: { skipped: boolean, deducted?: array }) => void
 *      Called after the user finishes (deduct or skip). Caller should then
 *      perform the original status change (PATCH prep_status etc.).
 *   onCancel: () => void
 *      Called when user dismisses the modal entirely (no status change either).
 */
export function DeductStockDialog({ shipmentId, open, onResolved, onCancel }) {
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [autoEnabled, setAutoEnabled] = useState(true);
  // checked[inventory_id] = bool ; qty[inventory_id] = number
  const [checked, setChecked] = useState({});
  const [qtyMap, setQtyMap] = useState({});

  useEffect(() => {
    if (!open || !shipmentId) return;
    let cancelled = false;
    setLoading(true);
    setSuggestions([]);
    setChecked({});
    setQtyMap({});
    api
      .get(`/shipments/${shipmentId}/inventory-suggestions`)
      .then(({ data }) => {
        if (cancelled) return;
        setSuggestions(data || []);
        const c = {};
        const q = {};
        for (const s of data || []) {
          c[s.inventory_id] = true;
          q[s.inventory_id] = s.suggested_qty;
        }
        setChecked(c);
        setQtyMap(q);
        // No matches → bail out silently so caller can proceed
        if (!data || data.length === 0) {
          onResolved({ skipped: true, deducted: [] });
        }
      })
      .catch(() => {
        if (cancelled) return;
        // Don't block the workflow if suggestions fail
        onResolved({ skipped: true, deducted: [] });
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shipmentId, open]);

  if (!open) return null;
  if (loading) {
    return (
      <div
        className="fixed inset-0 z-50 grid place-items-center bg-slate-900/60 backdrop-blur-sm"
        data-testid="deduct-stock-modal-loading"
      >
        <div className="rg-card p-6 bg-white flex items-center gap-3">
          <Loader2 className="h-5 w-5 animate-spin text-slate-700" />
          <span className="font-bold uppercase text-sm tracking-wide">Checking inventory…</span>
        </div>
      </div>
    );
  }
  if (!suggestions || suggestions.length === 0) return null;

  const toggleRow = (id) => setChecked((m) => ({ ...m, [id]: !m[id] }));
  const setQty = (id, v) => {
    const n = Math.max(1, parseInt(v, 10) || 1);
    setQtyMap((m) => ({ ...m, [id]: n }));
  };

  const selectedItems = suggestions.filter((s) => autoEnabled && checked[s.inventory_id]);
  const anyWouldGoNegative = selectedItems.some(
    (s) => qtyMap[s.inventory_id] > s.current_stock
  );

  const onDeduct = async () => {
    if (busy) return;
    if (!autoEnabled || selectedItems.length === 0) {
      // Treat as skip
      toast.message("No items selected — skipping deduction");
      onResolved({ skipped: true, deducted: [] });
      return;
    }
    setBusy(true);
    try {
      const body = {
        items: selectedItems.map((s) => ({
          inventory_id: s.inventory_id,
          quantity: qtyMap[s.inventory_id],
        })),
        allow_negative: false,
      };
      const { data } = await api.post(`/shipments/${shipmentId}/deduct-inventory`, body);
      const summary = (data.deducted || [])
        .map((d) => `${d.deducted_qty}x ${d.item_name}`)
        .join(", ");
      toast.success(summary ? `Deducted ${summary}` : "Stock deducted");
      if ((data.went_negative || []).length > 0) {
        toast.warning(
          `Warning: ${data.went_negative.length} item(s) clamped to 0 — was below requested qty.`
        );
      }
      if ((data.low_stock_triggered || []).length > 0) {
        const names = data.low_stock_triggered.map((l) => l.item_name).join(", ");
        toast.warning(`Low stock now: ${names}`);
      }
      onResolved({ skipped: false, deducted: data.deducted || [] });
    } catch (e) {
      toast.error("Could not deduct stock — saved without deduction");
      onResolved({ skipped: true, deducted: [] });
    } finally {
      setBusy(false);
    }
  };

  const onSkip = () => {
    if (busy) return;
    onResolved({ skipped: true, deducted: [] });
  };

  return (
    <div
      className="fixed inset-0 z-50 grid place-items-end sm:place-items-center bg-slate-900/60 backdrop-blur-sm p-0 sm:p-4"
      data-testid="deduct-stock-modal"
      onClick={(e) => {
        // dismiss on backdrop click only when not busy
        if (e.target === e.currentTarget && !busy) onCancel?.();
      }}
    >
      <div
        className="bg-white w-full sm:max-w-2xl rounded-t-lg sm:rounded-md border-t-4 sm:border-2 border-slate-900 shadow-2xl flex flex-col max-h-[92vh]"
        role="dialog"
        aria-modal="true"
      >
        {/* Header */}
        <div className="px-5 py-4 border-b-2 border-slate-200 flex items-start gap-3">
          <div className="h-10 w-10 grid place-items-center bg-slate-900 text-amber-400 rounded-sm flex-shrink-0">
            <Boxes strokeWidth={2.5} className="h-5 w-5" />
          </div>
          <div className="min-w-0 flex-1">
            <h2 className="font-display text-lg sm:text-xl uppercase text-slate-900 leading-tight">
              Deduct Stock from Inventory?
            </h2>
            <p className="text-xs sm:text-sm text-slate-600 mt-0.5">
              We matched {suggestions.length} item{suggestions.length === 1 ? "" : "s"} from this
              shipment to your inventory. Edit quantities or uncheck rows you don&apos;t want to
              deduct.
            </p>
          </div>
        </div>

        {/* Auto-deduct toggle */}
        <div className="px-5 py-3 bg-amber-50 border-b-2 border-amber-200 flex items-center gap-3">
          <input
            id="auto-deduct-toggle"
            type="checkbox"
            checked={autoEnabled}
            onChange={(e) => setAutoEnabled(e.target.checked)}
            data-testid="auto-deduct-toggle"
            className="h-5 w-5 accent-slate-900"
          />
          <label htmlFor="auto-deduct-toggle" className="text-sm font-bold text-slate-800 cursor-pointer flex-1">
            Auto-deduct these items
          </label>
        </div>

        {/* Items list */}
        <div className="flex-1 overflow-y-auto" data-testid="deduct-stock-list">
          <ul className="divide-y-2 divide-slate-100">
            {suggestions.map((s) => {
              const isChecked = !!checked[s.inventory_id];
              const qty = qtyMap[s.inventory_id] ?? s.suggested_qty;
              const willGoNegative = qty > s.current_stock;
              return (
                <li
                  key={s.inventory_id}
                  data-testid={`deduct-row-${s.inventory_id}`}
                  className={`px-5 py-3 flex items-center gap-3 ${!autoEnabled ? "opacity-50" : ""}`}
                >
                  <input
                    type="checkbox"
                    checked={isChecked}
                    onChange={() => toggleRow(s.inventory_id)}
                    disabled={!autoEnabled}
                    data-testid={`deduct-row-checkbox-${s.inventory_id}`}
                    className="h-5 w-5 accent-slate-900 flex-shrink-0"
                  />
                  <div className="flex-1 min-w-0">
                    <div className="font-bold text-slate-900 truncate flex items-center gap-2 flex-wrap">
                      <span>{s.item_name}</span>
                      <span className="text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 bg-slate-100 text-slate-700 border border-slate-300 rounded-sm">
                        {s.item_type || "Item"}
                      </span>
                      {s.stock_level === "red" && (
                        <span className="text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 bg-rose-100 text-rose-800 border border-rose-300 rounded-sm">
                          Low
                        </span>
                      )}
                    </div>
                    <div className="text-xs text-slate-600 mt-0.5">
                      In stock: <strong>{s.current_stock}</strong>
                      {s.matched_tokens?.length > 0 && (
                        <>
                          {" · "}
                          matched on &quot;{s.matched_tokens.join(", ")}&quot;
                        </>
                      )}
                    </div>
                    {willGoNegative && isChecked && autoEnabled && (
                      <div className="text-xs text-rose-700 mt-1 flex items-center gap-1 font-bold">
                        <AlertTriangle className="h-3.5 w-3.5" />
                        Only {s.current_stock} on hand — will be clamped to 0.
                      </div>
                    )}
                  </div>
                  <input
                    type="number"
                    min="1"
                    value={qty}
                    onChange={(e) => setQty(s.inventory_id, e.target.value)}
                    disabled={!autoEnabled || !isChecked}
                    data-testid={`deduct-row-qty-${s.inventory_id}`}
                    className="w-20 h-11 text-center font-bold font-mono border-2 border-slate-300 rounded-sm bg-white focus:border-slate-900 outline-none disabled:opacity-50 disabled:bg-slate-100"
                  />
                </li>
              );
            })}
          </ul>
        </div>

        {/* Buttons */}
        <div className="p-3 sm:p-4 border-t-2 border-slate-200 grid grid-cols-1 sm:grid-cols-2 gap-2 bg-white">
          <button
            type="button"
            onClick={onSkip}
            disabled={busy}
            data-testid="deduct-skip-btn"
            className="rg-tap-xl w-full bg-white text-slate-800 font-display uppercase tracking-wider rounded-sm flex items-center justify-center gap-2 disabled:opacity-60 min-h-[60px] text-base border-2 border-slate-300 hover:border-slate-700 order-2 sm:order-1"
          >
            <SkipForward strokeWidth={3} className="h-5 w-5" />
            Skip Deduction
          </button>
          <button
            type="button"
            onClick={onDeduct}
            disabled={busy || (autoEnabled && selectedItems.length === 0)}
            data-testid="deduct-confirm-btn"
            className="rg-tap-xl w-full bg-green-700 hover:bg-green-800 text-white font-display uppercase tracking-wider rounded-sm flex items-center justify-center gap-2 disabled:opacity-60 min-h-[60px] text-base border-2 border-green-900 order-1 sm:order-2"
          >
            {busy ? (
              <Loader2 className="h-5 w-5 animate-spin" />
            ) : (
              <PackageMinus strokeWidth={3} className="h-5 w-5" />
            )}
            {busy
              ? "Deducting…"
              : autoEnabled && selectedItems.length > 0
              ? `Deduct & Save (${selectedItems.length})`
              : "Deduct & Save"}
          </button>
        </div>
        {anyWouldGoNegative && (
          <div className="px-4 pb-3 text-[11px] text-amber-800 bg-amber-50 -mt-1 border-t border-amber-200">
            ⚠ One or more items don&apos;t have enough on hand — those will be clamped to 0.
          </div>
        )}
      </div>
    </div>
  );
}

export default DeductStockDialog;
